/**
 * The screen's wiring: one read, the scope in the URL, the search box, the facets and the order.
 *
 * Decides nothing. Every decision it composes lives in `model/` or in
 * `entities/exercise-library` — which is what makes the screen's behaviour testable without a
 * renderer and this file readable in one pass.
 *
 * ── ONE read, and it is the entity's own ────────────────────────────────────────────────────
 * `GET /exercise?lang=` fetch-all, cached thirty minutes and shared with the builder's rail and the
 * movement picker. Nothing here asks the backend to filter or to sort: the catalogue is a few
 * hundred short records, it is already in the browser, and filtering it client-side is what makes
 * "nothing matched" and "you have no exercises" distinguishable without a second request.
 *
 * The response is projected to `ExerciseDetail`, not to `ExerciseListItem` — ONE pass that answers
 * both questions, because `ExerciseDetail extends ExerciseListItem`. The header's `Z materiałami`
 * figure needs the links the row drops, and the preview panel is handed the full description of
 * whichever movement is selected.
 *
 * ── The SCOPE and the Źródło facet are one state with two affordances ───────────────────────
 * The canon draws both: a radio row with counts above the table, and a `Źródło` tab inside the
 * filter panel. They are not two filters — they are one value, the catalogue's own `origin` facet,
 * and both controls write it. The state lives in the URL because the switch is the one thing on
 * this screen a trainer sends to a colleague.
 *
 * That is also why the toolbar's dot counts `muscles + equipment + types` and NOT `origin`: the
 * scope switch is already saying which list this is, in words, four inches above the square. A dot
 * that lit merely because the trainer is on `Twoje` would be the square claiming a filter the
 * trainer can already see, with `Wyczyść` the only way out of something that is not in their way.
 *
 * ── The SKELETON GATE lives here, so `status` already carries the minimum life ───────────
 * Moved out of the screen on 21 September 2026. `status` is the read's three states with the gate's
 * tail folded in: it stays `loading` until the bars have had their 300ms, not until the answer
 * lands. `exerciseListStatus` asks `read === 'loading'` FIRST, so the table's six-way fork, the
 * preview column, the scope switch's counts and the toolbar's disabled state all turn at the same
 * moment the bars retire — without any of them knowing a gate exists.
 *
 * Forking on the read's own `isPending` instead is the defect `useSkeletonGate`'s header describes:
 * for an answer landing between 150 and 450ms the bars flash away when it arrives and the content
 * already on screen blinks to transparent and fades back in 300ms later. Two entrances, one load.
 */

import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { useSkeletonGate } from '~/shared/lib/useSkeletonGate';

import {
  emptyExerciseQuery,
  exerciseCatalogue,
  exerciseLibraryQueryOptions,
  toExerciseDetails,
  useOtherLanguageNames,
  type ExerciseCatalogueView,
  type ExerciseDetail,
  type ExerciseQuery,
} from '~/entities/exercise-library';

import { tagUsageQueryOptions } from '../api/exerciseTagRequests';
import { exerciseHeaderStats, type ExerciseHeaderStats } from '../model/exerciseHeaderStats';
import {
  exerciseScopeOf,
  exerciseScopeSearch,
  validateExerciseLibrarySearch,
} from '../model/exerciseLibrarySearch';
import { exerciseListStatus, type ExerciseListStatus } from '../model/exerciseListStatus';
import { reviewView } from '../model/exerciseReview';
import { toExerciseRows, type ExerciseRow, type TranslateKey } from '../model/exerciseRow';
import {
  exerciseScopeCounts,
  originOfScope,
  type ExerciseScope,
  type ExerciseScopeCounts,
} from '../model/exerciseScope';
import {
  DEFAULT_EXERCISE_SORT,
  pressSortColumn,
  sortExerciseRows,
  type ExerciseSort,
  type ExerciseSortColumn,
} from '../model/exerciseSort';
import { useContentLanguage } from '~/shared/i18n/contentLanguage';

export interface ExerciseLibraryState {
  /**
   * The read's three states WITH the skeleton gate's tail: `loading` until the bars have had their
   * minimum life. Everything that forks on the wait forks on this — see the header.
   */
  readonly status: 'loading' | 'error' | 'ready';
  /** Whether the bars belong inside the wait. False for its first 150ms. */
  readonly skeleton: boolean;
  /** The hand-over class the content that REPLACES the bars carries, or `''` when none was drawn. */
  readonly reveal: string;
  readonly listStatus: ExerciseListStatus;
  /** What the table draws: narrowed, then ordered. */
  readonly rows: readonly ExerciseRow[];
  /** The full description of every movement, by id — what the preview panel is handed. */
  readonly detailsById: ReadonlyMap<number, ExerciseDetail>;
  /** The header's three figures, or `null` while they are not known. */
  readonly stats: ExerciseHeaderStats | null;
  /** The figure beside each of the three scope words. */
  readonly scopeCounts: ExerciseScopeCounts;
  readonly scope: ExerciseScope;
  readonly setScope: (scope: ExerciseScope) => void;
  /** The search term and the three facets. `origin` is the scope and is written through `setScope`. */
  readonly query: ExerciseQuery;
  readonly setQuery: (query: ExerciseQuery) => void;
  /** The facet options with their counts, as the panel draws them. */
  readonly catalogue: ExerciseCatalogueView;
  /**
   * How many tags the trainer has, from `GET /exercise/tags` — a separate read that usually answers
   * before the catalogue. `null` while it has not answered; a failed read is `0`. What lets the wait reserve the tag bar and the
   * Tagi column before the catalogue says the same thing.
   */
  readonly tagCount: number | null;
  /** How many facets are narrowing, NOT counting the scope. The toolbar's dot. */
  readonly activeFacetCount: number;
  /** Every movement, before the query narrows it — what a tag merge's figure is counted over. */
  readonly items: readonly ExerciseDetail[];
  readonly sort: ExerciseSort;
  readonly pressColumn: (column: ExerciseSortColumn) => void;
  readonly retry: () => void;
  /**
   * Board D's toolbar toggle „Do uzupełnienia" (R17): how many movements of the list on screen carry
   * the review mark (`null` until the catalogue is read, never a false zero), whether only those are
   * shown, the press, and „Pokaż wszystkie", which lets go of it.
   */
  readonly review: { readonly count: number | null; readonly only: boolean; readonly toggle: () => void; readonly showAll: () => void };
  /** The search's other-language names could not be read; a „nothing matched" says so. */
  readonly otherLanguageFailed: boolean;
}

/** The query with no term and no facet, for `Wyczyść`. The scope is untouched — it is the URL's. */
const NO_FACETS = emptyExerciseQuery();

export function useExerciseLibrary(): ExerciseLibraryState {
  const { t, i18n } = useTranslation();
  // The read carries the content language; the collators below stay on the reader's own locale —
  // the table sorts translated type and equipment labels as well as exercise names.
  const contentLang = useContentLanguage();
  const navigate = useNavigate();

  // `strict: false` and re-validated through the slice's OWN validator rather than cast: one
  // definition of which parameters this screen understands, and the read is as safe as a strict one.
  const scope = exerciseScopeOf(validateExerciseLibrarySearch(useSearch({ strict: false })));

  // The term and the three facets. `origin` on this object is derived from the URL below and is
  // never stored here — one value cannot live in two places and stay in step.
  const [facets, setFacets] = useState<ExerciseQuery>(NO_FACETS);
  const [sort, setSort] = useState<ExerciseSort>(DEFAULT_EXERCISE_SORT);
  // A way of looking at the list, like the facets, so it stays out of the address with them.
  const [reviewOnly, setReviewOnly] = useState(false);

  const read = useQuery(exerciseLibraryQueryOptions(contentLang));
  const tagUsage = useQuery(tagUsageQueryOptions());
  // The search's second language. Never gates the screen: an empty map until it answers.
  const otherNames = useOtherLanguageNames();
  // The screen's one gate. `waiting` rather than `read.isPending`: the wait branch has to outlive
  // the answer by the bars' minimum life, or the table lands at full opacity and then fades in
  // again 300ms later. See the header.
  const gate = useSkeletonGate(read.isPending);
  const status: ExerciseLibraryState['status'] = gate.waiting
    ? 'loading'
    : read.isError
      ? 'error'
      : 'ready';

  /* ── The four passes over the catalogue are MEMOISED BY HAND, and that is not a reflex ──────
   *
   * The React Compiler is on and memoisation is its job everywhere else in this app. It does not
   * take this chain: read back off the dev server on 18 September 2026, the compiled hook caches
   * the query options, the `t` wrapper and the four callbacks, and leaves `toExerciseDetails`,
   * `exerciseCatalogue`, `toExerciseRows`/`sortExerciseRows` and `exerciseScopeCounts` to run on
   * every render — the compiler caches allocations and JSX, and a call into a module it cannot see
   * is not one.
   *
   * What that cost: `pointedKey` lives in the screen that calls this hook, so EVERY pointer move
   * re-projected 479 records, re-filtered and re-collated them, re-projected 479 rows with several
   * `t()` lookups each and re-sorted the result — three full passes and about two thousand
   * translations before React began reconciling. Profiled on the real catalogue: ~2.6s busy over
   * twelve hovers, of which i18next alone was ~360ms. It also handed the table 479 BRAND-NEW row
   * objects each time, which is what kept every row re-rendering however the rows were componentised
   * — 4790 row objects rebuilt across a 20-row walk, measured.
   *
   * The deps are exactly what each pass reads. `t` is react-i18next's own function and is stable
   * until the language changes; the key-only lambda over it was already cached by the compiler on
   * `t`, so it is the one link in the chain that never needed help. */
  const translate = useCallback<TranslateKey>((key) => t(key), [t]);

  const details = useMemo(() => toExerciseDetails(read.data), [read.data]);
  const query: ExerciseQuery = useMemo(
    () => ({ ...facets, origin: originOfScope(scope) }),
    [facets, scope],
  );
  const catalogue = useMemo(
    () => exerciseCatalogue(details, query, i18n.language, otherNames.names),
    [details, query, i18n.language, otherNames.names],
  );
  const review = useMemo(() => reviewView(catalogue.results, reviewOnly), [catalogue.results, reviewOnly]);
  const rows = useMemo(
    () => sortExerciseRows(toExerciseRows(review.shown, translate), sort, i18n.language),
    [review.shown, translate, sort, i18n.language],
  );

  // The SCOPE's own population, before the term and the facets narrow it — which is what tells
  // "you have no exercises of your own" from "nothing matched what you typed".
  const scopeCounts = useMemo(() => exerciseScopeCounts(details), [details]);
  const scopeCount = scopeCounts[scope];

  // The theatre's lookup table. One `Map` per catalogue rather than one per render, for the same
  // reason as the four above: it is handed to `useTheatre`, which hands it on.
  const detailsById = useMemo(
    () => new Map(details.map((detail) => [detail.id, detail])),
    [details],
  );

  // Without `origin`: the catalogue's own figure less the scope, so a selected tag that no movement
  // carries any more is not counted either.
  const activeFacetCount = catalogue.activeCount - (query.origin === 'all' ? 0 : 1);

  return {
    status,
    skeleton: gate.skeleton,
    reveal: gate.reveal,
    listStatus: exerciseListStatus({
      read: status,
      scope,
      catalogueCount: details.length,
      scopeCount,
      rowCount: rows.length,
      reviewOnly,
      narrowed: facets.text.trim() !== '' || activeFacetCount > 0,
    }),
    rows,
    detailsById,
    // Never a zero while the read is open or after it failed: "0 · 0 · 0" beside a failure card is
    // three false statements about the trainer's own account.
    stats: exerciseHeaderStats(status === 'ready' ? details : null),
    scopeCounts,
    scope,
    setScope: (next) => {
      // WHOLE, never spread over the current search: spreading carries the key this is dropping,
      // and it would carry an open card describing a movement the new list may not hold.
      void navigate({ to: '/cwiczenia', search: exerciseScopeSearch(next) });
    },
    query,
    setQuery: (next) => {
      // The scope is the URL's. A panel writing `origin` reaches `setScope`, and this setter keeps
      // only what it owns, so the two can never disagree about which list is on screen.
      setFacets({ ...next, origin: 'all' });
    },
    catalogue,
    // Without `origin` — see the header.
    activeFacetCount,
    items: details,
    sort,
    pressColumn: (column) => {
      setSort(pressSortColumn(sort, column));
    },
    // A failed tags read counts as none: the catalogue still draws the bar if there are tags.
    tagCount: tagUsage.isError ? 0 : (tagUsage.data?.length ?? null),
    otherLanguageFailed: otherNames.failed,
    review: {
      count: status === 'ready' ? review.count : null,
      only: reviewOnly,
      toggle: () => {
        setReviewOnly((current) => !current);
      },
      showAll: () => {
        setReviewOnly(false);
      },
    },
    retry: () => {
      void read.refetch();
    },
  };
}
